import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  InputAdornment
} from '@mui/material';
import {
  Public as PublicIcon,
  Lock as LockIcon,
  PhotoLibrary as GalleryIcon
} from '@mui/icons-material';

const CreateGalleryDialog = ({ open, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isPublic: true
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Gallery name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Gallery name cannot exceed 100 characters';
    }
    
    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description cannot exceed 500 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSave(formData);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      name: '',
      description: '',
      isPublic: true
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Gallery</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <TextField
            name="name"
            label="Gallery Name"
            value={formData.name}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.name}
            helperText={errors.name || `${formData.name.length}/100 characters`}
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <GalleryIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <TextField
            name="description"
            label="Description"
            value={formData.description}
            onChange={handleChange}
            fullWidth
            multiline
            rows={3}
            margin="normal"
            error={!!errors.description}
            helperText={errors.description || `${formData.description.length}/500 characters`}
            inputProps={{ maxLength: 500 }}
          />
          
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  name="isPublic"
                  checked={formData.isPublic}
                  onChange={handleSwitchChange}
                  color="primary"
                />
              }
              label={
                <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                  {formData.isPublic ? (
                    <>
                      <PublicIcon sx={{ mr: 1 }} />
                      Public Gallery
                    </>
                  ) : (
                    <>
                      <LockIcon sx={{ mr: 1 }} />
                      Private Gallery
                    </>
                  )}
                </Typography>
              }
            />
            <Typography variant="caption" color="text.secondary" display="block">
              {formData.isPublic 
                ? "Anyone can view this gallery" 
                : "Only you can see this gallery"}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={!formData.name.trim()}
        >
          Create Gallery
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateGalleryDialog;
